import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Loader2, Lock } from "lucide-react";

const SDK = { sandbox: "https://sandbox.web.squarecdn.com/v1/square.js", production: "https://web.squarecdn.com/v1/square.js" };
let sdkPromise = null;
const loadSdk = (env) => {
  if (window.Square) return Promise.resolve(window.Square);
  sdkPromise = sdkPromise || new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SDK[env];
    s.onload = () => resolve(window.Square);
    s.onerror = () => { sdkPromise = null; reject(new Error("Could not load the card form")); };
    document.head.appendChild(s);
  });
  return sdkPromise;
};

// Square's secure card field. Card details never touch our servers; tokenize() returns a one-time token.
export const SquareCard = forwardRef(({ square, contact }, ref) => {
  const card = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const Sq = await loadSdk(square.env);
        const c = await Sq.payments(square.app_id, square.location_id).card();
        if (!alive) { c.destroy(); return; }
        await c.attach("#sq-card");
        card.current = c;
        setStatus("ready");
      } catch { if (alive) setStatus("error"); }
    })();
    return () => { alive = false; card.current?.destroy(); card.current = null; };
  }, [square.env, square.app_id, square.location_id]);

  useImperativeHandle(ref, () => ({
    tokenize: async () => {
      if (!card.current) throw new Error("The card form isn't ready yet");
      const r = await card.current.tokenize({ billingContact: { ...contact, countryCode: "AU" }, intent: "STORE", customerInitiated: true, sellerKeyedIn: false });
      if (r.status !== "OK") throw new Error(r.errors?.[0]?.message || "Check your card details");
      return r.token;
    },
  }));

  return (
    <div className="space-y-1.5" data-testid="square-card">
      <div id="sq-card" className="min-h-[56px]" data-testid="square-card-form" />
      {status === "loading" && <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading secure card form…</p>}
      {status === "error" && <p className="text-xs text-destructive" data-testid="square-card-error">The card form couldn&apos;t load. Refresh the page and try again.</p>}
      {status === "ready" && <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Card details are handled securely by Square.</p>}
    </div>
  );
});
SquareCard.displayName = "SquareCard";
